/**
 * Permission Configuration
 * Maps routes and menu items to required permissions and roles
 *
 * Note: super_admin and admin have full access by default
 * This configuration primarily affects manager role
 */

import { MODULES, ROLES, RoutePermission, MenuPermission } from "./types";

/**
 * Route Permissions Configuration
 * Each route specifies which backend permissions are required to access it
 */
export const ROUTE_PERMISSIONS: RoutePermission[] = [
  // Dashboard - accessible to all
  {
    path: "/dashboard",
    module: MODULES.DASHBOARD,
    requiredPermissions: [], // No specific permission needed
  },

  // Crop & Disease Management
  {
    path: "/all-crop",
    module: MODULES.CROP,
    requiredPermissions: ["cropList", "cropActiveList", "cropListLimit"],
  },
  {
    path: "/crop-category",
    module: MODULES.CROP_CATEGORY,
    requiredPermissions: ["cropCategoryList", "cropCategoryActiveList"],
  },
  {
    path: "/disease-insect",
    module: MODULES.DISEASE_INSECT,
    requiredPermissions: ["diseaseAndPestList", "diseaseAndPestActiveList"],
  },
  {
    path: "/disease-detection",
    module: MODULES.DISEASE_DETECTION,
    requiredPermissions: ["diseaseDetectionList"],
  },

  // Product Management
  {
    path: "/products",
    module: MODULES.PRODUCT,
    requiredPermissions: [
      "productList",
      "productActiveList",
      "productListLimit",
    ],
  },
  {
    path: "/product-category",
    module: MODULES.PRODUCT_CATEGORY,
    requiredPermissions: ["productCategoryList", "productCategoryActiveList"],
  },
  {
    path: "/product-sub-category",
    module: MODULES.PRODUCT_SUB_CATEGORY,
    requiredPermissions: ["productSubCategoryList"],
  },
  {
    path: "/product-company",
    module: MODULES.PRODUCT_COMPANY,
    requiredPermissions: ["productCompanyList", "productCompanyActiveList"],
  },
  {
    path: "/product-element",
    module: MODULES.PRODUCT_ELEMENT,
    requiredPermissions: ["productElementList", "productElementActiveList"],
  },
  {
    path: "/product-solution",
    module: MODULES.PRODUCT_SOLUTION,
    requiredPermissions: ["productSolutionList", "productSolutionActiveList"],
  },
  {
    path: "/product-size",
    module: MODULES.PRODUCT_SIZE,
    requiredPermissions: ["productSizeList"],
  },
  {
    path: "/product-unit-type",
    module: MODULES.PRODUCT_UNIT,
    requiredPermissions: ["productUnitList"],
  },
  {
    path: "/product-color-code",
    module: MODULES.PRODUCT_COLOR_CODE,
    requiredPermissions: ["productColorCodeList"],
  },
  {
    path: "/generic-group",
    module: MODULES.GENERIC_GROUP,
    requiredPermissions: ["genericGroupList", "genericGroupActiveList"],
  },
  {
    path: "/working-mood",
    module: MODULES.WORKING_MOOD,
    requiredPermissions: ["workingMoodList", "workingMoodActiveList"],
  },

  // Order Management
  {
    path: "/order/pending-order",
    module: MODULES.ORDER_PENDING,
    requiredPermissions: ["orderList", "orderPendingList"],
  },
  {
    path: "/order/approved-order",
    module: MODULES.ORDER_APPROVED,
    requiredPermissions: ["orderList", "orderApprovedList"],
  },
  {
    path: "/order/complete-order",
    module: MODULES.ORDER_COMPLETE,
    requiredPermissions: ["orderList", "orderCompleteList"],
  },
  {
    path: "/order/cancelled-order",
    module: MODULES.ORDER_CANCELLED,
    requiredPermissions: ["orderList", "orderCancelledList"],
  },
  {
    path: "/order/return-or-refund",
    module: MODULES.ORDER_RETURN_REFUND,
    requiredPermissions: ["orderList", "orderReturnList"],
  },
  {
    path: "/order/manual-order",
    module: MODULES.ORDER_MANUAL,
    requiredPermissions: ["orderList", "orderManualAdd"],
  },

  // Stock Management
  {
    path: "/stock",
    module: MODULES.STOCK,
    requiredPermissions: ["stockList", "stockListLimit"],
  },

  // Early Warning
  {
    path: "/warning-category",
    module: MODULES.WARNING_CATEGORY,
    requiredPermissions: ["warningCategoryList"],
  },
  {
    path: "/early-warning",
    module: MODULES.EARLY_WARNING,
    requiredPermissions: ["earlyWarningList"],
  },

  // Course Management
  {
    path: "/course",
    module: MODULES.COURSE,
    requiredPermissions: ["courseList", "courseActiveList"],
  },
  {
    path: "/course-category",
    module: MODULES.COURSE_CATEGORY,
    requiredPermissions: ["courseCategoryList"],
  },

  // Dealer Management
  {
    path: "/dealer",
    module: MODULES.DEALER,
    requiredPermissions: ["dealerList", "dealerActiveList"],
  },
  {
    path: "/pending-dealer",
    module: MODULES.PENDING_DEALER,
    requiredPermissions: ["dealerList", "pendingDealerList"],
  },
  {
    path: "/approved-payment-method",
    module: MODULES.PAYMENT_METHOD_APPROVED,
    requiredPermissions: ["paymentMethodList", "approvedPaymentMethodList"],
  },
  {
    path: "/pending-payment-method",
    module: MODULES.PAYMENT_METHOD_PENDING,
    requiredPermissions: ["paymentMethodList", "pendingPaymentMethodList"],
  },

  // Configuration
  {
    path: "/banner",
    module: MODULES.BANNER,
    requiredPermissions: ["bannerList", "bannerActiveList"],
  },
  {
    path: "/chad-bagan-tips",
    module: MODULES.CHAD_BAGAN_TIPS,
    requiredPermissions: ["chadBaganTipsList"],
  },
  {
    path: "/faq",
    module: MODULES.FAQ,
    requiredPermissions: ["faqList", "faqActiveList"],
  },
  {
    path: "/farmer-safety",
    module: MODULES.FARMER_SAFETY,
    requiredPermissions: ["farmerSafetyList"],
  },
  {
    path: "/feature",
    module: MODULES.FEATURE,
    requiredPermissions: ["featureList", "featureActiveList"],
  },
  {
    path: "/gallery",
    module: MODULES.GALLERY,
    requiredPermissions: ["mediaList", "mediaActiveList"],
  },
  {
    path: "/profession",
    module: MODULES.PROFESSION,
    requiredPermissions: ["professionList"],
  },
  {
    path: "/reference",
    module: MODULES.REFERENCE,
    requiredPermissions: ["referenceList"],
  },
  {
    path: "/team-consultant",
    module: MODULES.TEAM_CONSULTANT,
    requiredPermissions: ["teamConsultantList"],
  },
  {
    path: "/district",
    module: MODULES.DISTRICT,
    requiredPermissions: ["districtList"],
  },
  {
    path: "/division",
    module: MODULES.DIVISION,
    requiredPermissions: ["divisionList"],
  },
];

/**
 * Menu Permissions Configuration
 * Each menu item specifies which permissions are required to see it
 */
export const MENU_PERMISSIONS: MenuPermission[] = [
  // Dashboard
  {
    key: "dashboard",
    module: MODULES.DASHBOARD,
    requiredPermissions: [],
  },

  // Crop & Disease Menu Group
  {
    key: "crop_disease_group",
    module: MODULES.CROP,
    requiredPermissions: ["cropList", "diseaseAndPestList"],
  },
  {
    key: "crop_submenu",
    module: MODULES.CROP,
    requiredPermissions: ["cropList"],
  },
  {
    key: "all_crop",
    module: MODULES.CROP,
    requiredPermissions: ["cropList"],
  },
  {
    key: "crop_category",
    module: MODULES.CROP_CATEGORY,
    requiredPermissions: ["cropCategoryList"],
  },
  {
    key: "disease_insect",
    module: MODULES.DISEASE_INSECT,
    requiredPermissions: ["diseaseAndPestList"],
  },
  {
    key: "disease_detection",
    module: MODULES.DISEASE_DETECTION,
    requiredPermissions: ["diseaseDetectionList"],
  },

  // Product Menu Group
  {
    key: "product_group",
    module: MODULES.PRODUCT,
    requiredPermissions: ["productList"],
  },
  {
    key: "products",
    module: MODULES.PRODUCT,
    requiredPermissions: ["productList"],
  },
  {
    key: "product_settings",
    module: MODULES.PRODUCT,
    requiredPermissions: ["productCategoryList"],
  },
  {
    key: "product_category",
    module: MODULES.PRODUCT_CATEGORY,
    requiredPermissions: ["productCategoryList"],
  },
  {
    key: "product_sub_category",
    module: MODULES.PRODUCT_SUB_CATEGORY,
    requiredPermissions: ["productSubCategoryList"],
  },
  {
    key: "product_color_code",
    module: MODULES.PRODUCT_COLOR_CODE,
    requiredPermissions: ["productColorCodeList"],
  },
  {
    key: "product_company",
    module: MODULES.PRODUCT_COMPANY,
    requiredPermissions: ["productCompanyList"],
  },
  {
    key: "product_element",
    module: MODULES.PRODUCT_ELEMENT,
    requiredPermissions: ["productElementList"],
  },
  {
    key: "generic_group",
    module: MODULES.GENERIC_GROUP,
    requiredPermissions: ["genericGroupList"],
  },
  {
    key: "product_size",
    module: MODULES.PRODUCT_SIZE,
    requiredPermissions: ["productSizeList"],
  },
  {
    key: "product_solution",
    module: MODULES.PRODUCT_SOLUTION,
    requiredPermissions: ["productSolutionList"],
  },
  {
    key: "product_unit",
    module: MODULES.PRODUCT_UNIT,
    requiredPermissions: ["productUnitList"],
  },
  {
    key: "working_mood",
    module: MODULES.WORKING_MOOD,
    requiredPermissions: ["workingMoodList"],
  },

  // Order Menu Group
  {
    key: "order_group",
    module: MODULES.ORDER,
    requiredPermissions: ["orderList"],
  },
  {
    key: "order_pending",
    module: MODULES.ORDER_PENDING,
    requiredPermissions: ["orderList"],
  },
  {
    key: "order_approved",
    module: MODULES.ORDER_APPROVED,
    requiredPermissions: ["orderList"],
  },
  {
    key: "order_complete",
    module: MODULES.ORDER_COMPLETE,
    requiredPermissions: ["orderList"],
  },
  {
    key: "order_cancelled",
    module: MODULES.ORDER_CANCELLED,
    requiredPermissions: ["orderList"],
  },
  {
    key: "order_return_refund",
    module: MODULES.ORDER_RETURN_REFUND,
    requiredPermissions: ["orderList"],
  },
  {
    key: "order_manual",
    module: MODULES.ORDER_MANUAL,
    requiredPermissions: ["orderList"],
  },

  // Early Warning Menu Group
  {
    key: "early_warning_group",
    module: MODULES.EARLY_WARNING,
    requiredPermissions: ["earlyWarningList"],
  },
  {
    key: "warning_category",
    module: MODULES.WARNING_CATEGORY,
    requiredPermissions: ["warningCategoryList"],
  },
  {
    key: "early_warning",
    module: MODULES.EARLY_WARNING,
    requiredPermissions: ["earlyWarningList"],
  },

  // Stock
  {
    key: "stock",
    module: MODULES.STOCK,
    requiredPermissions: ["stockList"],
  },

  // Course Menu Group
  {
    key: "course_group",
    module: MODULES.COURSE,
    requiredPermissions: ["courseList"],
  },
  {
    key: "all_course",
    module: MODULES.COURSE,
    requiredPermissions: ["courseList"],
  },
  {
    key: "course_category",
    module: MODULES.COURSE_CATEGORY,
    requiredPermissions: ["courseCategoryList"],
  },

  // Dealer Menu Group
  {
    key: "dealer_group",
    module: MODULES.DEALER,
    requiredPermissions: ["dealerList"],
  },
  {
    key: "all_dealer",
    module: MODULES.DEALER,
    requiredPermissions: ["dealerList"],
  },
  {
    key: "pending_dealer",
    module: MODULES.PENDING_DEALER,
    requiredPermissions: ["dealerList"],
  },
  {
    key: "payment_method_group",
    module: MODULES.DEALER,
    requiredPermissions: ["paymentMethodList"],
  },
  {
    key: "payment_method_approved",
    module: MODULES.PAYMENT_METHOD_APPROVED,
    requiredPermissions: ["paymentMethodList"],
  },
  {
    key: "payment_method_pending",
    module: MODULES.PAYMENT_METHOD_PENDING,
    requiredPermissions: ["paymentMethodList"],
  },

  // Configuration Menu Group
  {
    key: "configuration_group",
    module: MODULES.BANNER,
    requiredPermissions: ["bannerList"],
  },
  {
    key: "banner",
    module: MODULES.BANNER,
    requiredPermissions: ["bannerList"],
  },
  {
    key: "chad_bagan_tips",
    module: MODULES.CHAD_BAGAN_TIPS,
    requiredPermissions: ["chadBaganTipsList"],
  },
  {
    key: "faq",
    module: MODULES.FAQ,
    requiredPermissions: ["faqList"],
  },
  {
    key: "farmer_safety",
    module: MODULES.FARMER_SAFETY,
    requiredPermissions: ["farmerSafetyList"],
  },
  {
    key: "feature",
    module: MODULES.FEATURE,
    requiredPermissions: ["featureList"],
  },
  {
    key: "gallery",
    module: MODULES.GALLERY,
    requiredPermissions: ["mediaList"],
  },
  {
    key: "profession",
    module: MODULES.PROFESSION,
    requiredPermissions: ["professionList"],
  },
  {
    key: "reference",
    module: MODULES.REFERENCE,
    requiredPermissions: ["referenceList"],
  },
  {
    key: "team_consultant",
    module: MODULES.TEAM_CONSULTANT,
    requiredPermissions: ["teamConsultantList"],
  },
];

/**
 * Helper function to get route permission by path
 */
export const getRoutePermission = (
  path: string,
): RoutePermission | undefined => {
  return ROUTE_PERMISSIONS.find((route) => {
    // Match exact path or wildcard path
    const routePath = route.path.replace("/*", "");
    return path.startsWith(routePath);
  });
};

/**
 * Helper function to get menu permission by key
 */
export const getMenuPermission = (key: string): MenuPermission | undefined => {
  return MENU_PERMISSIONS.find((menu) => menu.key === key);
};
